import type { Session, ResultNode } from "../engine";
import { formatFastStage, getComputedValue } from "../engine";
import { resolve as resolveCatalog } from "../i18n/catalogs";
import { buildPdfModel } from "./content";
import type { PdfModel } from "./content";

// Turn a completed live session into the PdfModel the renderer consumes. Pure: no DOM, no I/O.
// functionalStatus is surfaced from the rule set's primary computed value (PPS or FAST) when defined,
// so the emailed PDF shows the clinician the derived functional score.
export function buildModelFromSession(
  session: Session,
  result: ResultNode,
  assessmentDate: string,
  professional?: { label: string; value: string }[]
): PdfModel {
  const computed = session.computed ?? {};
  const functionalStatus = deriveFunctionalStatus(session);
  const resolver = (key: string) =>
    resolveCatalog(key, { locale: session.locale, audience: session.audience });

  const model = buildPdfModel(
    result,
    session.history,
    {
      conditionName: session.rules.metadata.name,
      assessmentDate,
      functionalStatus,
    },
    computed,
    resolver
  );

  if (professional && professional.length > 0) {
    model.professional = professional;
  }
  return model;
}

function deriveFunctionalStatus(session: Session): string | undefined {
  // The functional score (PPS/FAST) is clinical detail; keep it off the family document.
  if (session.audience !== "professional") return undefined;
  const defs = session.rules.computedValues;
  if (!defs) return undefined;
  if (defs["pps"]) {
    const pps = getComputedValue(session, "pps");
    return `Estimated PPS: ${Math.round(pps)}%`;
  }
  if (defs["fast"]) {
    const fast = getComputedValue(session, "fast");
    return `Estimated FAST stage: ${formatFastStage(fast)}`;
  }
  return undefined;
}
